import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { NoResultsFoundIcon } from '@/assets/icons/NoResultsFoundIcon';
import { PermissionGate } from '@/core/auth/PermissionGate/PermissionGate';
import { EntityType } from '@/core/graphql/generated/types';
import { paths } from '@/core/router/paths';
import { Permission } from '@/core/store/auth/auth.types';
import { DcrSearchCta } from '@/features/DcrForm/DcrSearchCta/DcrSearchCta';
import { InactiveOptInNudge } from '@/features/SearchAndFilters/InactiveRecords/InactiveOptInNudge/InactiveOptInNudge';
import { useQueryParams } from '@/utils/url/useQueryParams';

import { noSearchResultsStyles as sx } from './NoSearchResults.styles';

type Props = {
	query?: string;
	entityType?: EntityType;
	handleGoBack?: () => void;
	displayBackButton?: boolean;
	withIcon?: boolean;
	withName?: boolean;
	withCta?: boolean;
	withFiltersHint?: boolean;
	singleFilter?: boolean;
	fullHeight?: boolean;
	showInactiveNudge?: boolean;
	inactiveCount?: number;
};

export const NoSearchResults = ({
	query,
	entityType,
	displayBackButton = true,
	handleGoBack: goBack,
	withIcon = false,
	withName = false,
	withCta = false,
	withFiltersHint = true,
	singleFilter = false,
	fullHeight = false,
	showInactiveNudge = false,
	inactiveCount = 0
}: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const { name } = useQueryParams();
	const filterHint = singleFilter
		? t('remove the filter')
		: t('using different filters');

	const handleGoBack = () => {
		if (goBack) {
			return goBack?.();
		} else {
			return location.key !== 'default' ? navigate(-1) : navigate(paths.home);
		}
	};

	return (
		<Box sx={sx.root(fullHeight)}>
			{withIcon && <NoResultsFoundIcon sx={sx.searchIcon} />}

			<Box sx={sx.selectedFiltersMessage}>
				<Typography sx={sx.noResultsMain} color='textPrimary'>
					{withName && (query || name)
						? t("No results for '{{name}}'.", { name: query ? query : name })
						: t('No results found.')}
				</Typography>
				<Typography color='textPrimary' gutterBottom>
					{withName && (query || name)
						? withFiltersHint
							? t(
									'Check the spelling, try a different search term or {{filterHint}}.',
									{ filterHint }
								)
							: t('Check the spelling, or try a different search term.')
						: withFiltersHint
							? t('Try a different search term or {{filterHint}}.', {
									filterHint
								})
							: t('Try a different search term.')}
				</Typography>
			</Box>

			{withCta &&
				(showInactiveNudge && inactiveCount > 0 ? (
					<Box sx={sx.ctaContainer}>
						<InactiveOptInNudge
							count={inactiveCount}
							entityType={entityType ?? EntityType.Hcp}
						/>
					</Box>
				) : (
					<PermissionGate permission={Permission.DcrEnabled}>
						<Box sx={sx.ctaContainer}>
							<DcrSearchCta
								entityType={entityType ?? EntityType.Hcp}
								customSx={{
									header: {
										fontWeight: 500
									}
								}}
							/>
						</Box>
					</PermissionGate>
				))}

			{displayBackButton && (
				<Box sx={sx.backButtonContainer}>
					<Button
						startIcon={<NavigateBeforeIcon fontSize='small' />}
						sx={sx.backButton}
						aria-label={t('Back navigation')}
						onClick={handleGoBack}
					>
						{t('BACK')}
					</Button>
				</Box>
			)}
		</Box>
	);
};
